<template lang="pug">
  v-dialog(v-model="show"  persistent max-width="600px" scrollable :fullscreen="$vuetify.breakpoint.smAndDown" )
    v-snackbar( v-model="snackbar" :color="snackbarColor" ) {{snackBarText}}
      template( v-slot:action="{attrs}")
        v-btn(text v-bind="attrs"
          @click="snackbar = false")
          v-icon fal fa-times
    v-snackbar(v-model="showError" color="error")
      span {{ errorMessage }}
      v-btn(dark text @click="showError = false")
        v-icon fal fa-times
    DeleteDialog(v-if="showDeleteDialog" :show="showDeleteDialog" :currentUser="currentUser" @closeAndLogOut="closeDeleteDialogClick")
    v-dialog(v-model="showAutomaticOrderingConfirm" persistent max-width="480")
      v-card
        v-card-title {{$t('profileDialog.automaticOrderingConfirmTitle')}}
        v-card-text
          p {{$t('profileDialog.automaticOrderingConfirmText1')}}
          v-alert.mb-0(type="warning" text dense icon="fas fa-exclamation-triangle") {{$t('profileDialog.automaticOrderingConfirmText2')}}
        v-card-actions
          v-spacer
          v-btn(text @click="onAutomaticOrderingCancel" :disabled="automaticOrderingSaveInProgress") {{$t('profileDialog.cancelButtonLabel')}}
          v-btn(color="error" :loading="automaticOrderingSaveInProgress" @click="onAutomaticOrderingConfirm") {{$t('profileDialog.automaticOrderingConfirmButton')}}
    v-card.fill-height
      v-card-title.ma-0.pa-0
        v-toolbar(flat dark color="primary")
          v-toolbar-title {{$t('profileDialog.dialogTitle')}}
          v-spacer
          v-btn(icon dark @click="onCloseButtonClick" :disabled="isSaveInProgress")
            v-icon fal fa-times
        template(v-if="!showSkeletonLoader")
          v-tabs(show-arrows prev-icon="far fa-arrow-left" next-icon="far fa-arrow-right" v-model="tab")
            v-tab {{$t('profileDialog.ProfileTitle')}}
            v-tab {{$t('profileDialog.credentialsTitle')}}
      v-skeleton-loader.sloader(v-if="showSkeletonLoader" type="list-item-two-line@3")
      v-card-text(style="height: 54vh;" id="cardText" v-else )
        v-tabs-items(v-model="tab")
          v-tab-item
            v-card(flat :disabled="saveInProgress")
              v-card-text.pt-6.pa-0
                template(v-if="showAutomaticOrderingSwitch")
                  v-card-title.pa-0.mb-2 {{$t('profileDialog.settingsTitle')}}
                  v-switch.mt-0.mb-6(v-model="automaticOrdering"
                    :label="`${$t('profileDialog.automaticOrderingLabel')}`"
                    :hint="`${$t('profileDialog.automaticOrderingHint')}`"
                    persistent-hint
                    :loading="automaticOrderingSaveInProgress"
                    :disabled="automaticOrderingSaveInProgress"
                    @change="onAutomaticOrderingChange")
                  v-divider.mb-6
                v-card-title.pa-0.mb-2 {{$t('profileDialog.personalDataTitle')}}
                v-form(ref="form" v-model="isFormValid" :lazy-validation="true" :disabled="isSaveInProgress" @submit.prevent )
                  v-row.ma-0
                    v-col.pa-0(cols="12")
                      v-select(:label="`${$t('languages.language')}`"
                        item-value="code"
                        item-text="label"
                        :items="supportedLanguages"
                        v-model="selectedItem"
                        outlined
                        return-object
                        append-icon="fal fa-chevron-down"
                        @change="onLanguageSelect(selectedItem)"
                      )
                  v-row.ma-0
                    v-col.py-0.pa-0(cols="12" md="6")
                      v-text-field(v-model="firstName" :label="` ${$t('profileDialog.firstName')}`" :rules="firstNameRules" outlined type="text" clearable)
                    v-col.py-0.pa-0.pl-md-2(cols="12" md="6")
                      v-text-field(v-model="familyName" :label="` ${$t('profileDialog.familyName')}`" :rules="familyNameRules" outlined)
                  v-row.ma-0
                    v-col.py-0.pa-0(cols="12" md="6")
                      v-text-field(v-model="phoneNumber" :label="` ${$t('profileDialog.phoneNumber')}`" outlined hide-details :rules="validatePhoneNumber")
                  v-list.pa-0
                    v-subheader {{$t('profileDialog.email')}}
                    v-list-item(v-for="(item, idx) in emailList" :key="idx")
                      v-list-item-content.pa-0
                        v-list-item-title(v-if="!item.isEdit") {{ item.email }}
                        v-text-field(v-if="item.isEdit" v-model="item.email" :rules="emailRules" autofocus  @keyup.enter="onSaveChangesClick(idx, item)" )
                      v-list-item-action
                        v-row.ma-0.pa-0(v-if="item.isEdit" align="end")
                          v-col.pa-0
                            v-btn(fab text x-small icon @click="onSaveChangesClick(idx, item)" :disabled="!isFormValid")
                              v-icon fa-check
                          v-col.pa-0
                            v-btn(fab text x-small icon @click="onCancelChangesClick(idx, item)")
                              v-icon fa-times
                        v-row.ma-0.pa-0(v-if="!item.isEdit")
                          v-col.pa-0
                            v-btn(fab text x-small icon @click="onEditClick(idx, item)" :disabled="isSaveInProgress || isInEditMode")
                              v-icon fa-pen
                          v-col.pa-0
                            v-btn(fab text x-small icon @click="onDeleteClick(idx)" :disabled="isSaveInProgress")
                              v-icon fa-trash
                v-row.ma-0
                  v-col.text-start(cols="12")
                    v-btn(@click="onAddButtonClick" :disabled="!isFormValid || isInEditMode || isSaveInProgress || showSkeletonLoader" :loading="isSaveInProgress") {{$t('profileDialog.addEmailLabel')}}
                v-card-actions.pa-0.pb-2
                  v-spacer
                  v-btn(color="primary"
                    :disabled="!isFormValid || isInEditMode || showSkeletonLoader"
                    :loading="isSaveInProgress"
                    @click="onSubmitButtonClick") {{$t('profileDialog.saveButtonLabel')}}
            v-divider.mb-2.mt-2
            v-row.ma-0.heading.mt-2
              v-col.pa-0(cols="12")
                v-expansion-panels.expansion-panel(accordion )
                  v-expansion-panel.pa-0
                    v-expansion-panel-header.font-weight-bold.title( :loading="saveInProgress" expand-icon="fas fa-caret-down" @click="onExpansionClick") {{$t('profileDialog.deleteAccountTitle')}}
                    v-expansion-panel-content.ma-0(eager)
                      v-card.elevation-0(id="card")
                        v-card-text.pa-0
                          v-row.ma-0
                            v-col.pl-2(cols="12")
                              span.font-weight-bold.tab {{$t('profileDialog.deleteAccountResult1')}}
                            v-col.pl-2(cols="12")
                              span.font-weight-bold.tab {{$t('profileDialog.deleteAccountResult2')}}
                            v-col.pl-2(cols="12")
                              span.font-weight-bold.tab {{$t('profileDialog.deleteAccountResult3')}}
                          v-row.ma-0
                            v-col(cols="12")
                              v-text-field(
                                :class="getTextFieldColor"
                                :type="passwordFieldType"
                                :label="$t('loginPage.passwordFieldLabel')"
                                :append-icon="passwordFieldIcon" outlined
                                :rules="passwordRules"
                                :disabled="passwordChecked"
                                v-model="password"
                                :hint="$t('profileDialog.deleteAccountPasswordTitle')"
                                @click:append="onToggleShowPasswordClick")
                        v-card-actions
                          v-spacer
                          v-btn(v-if="passwordChecked" color="error" :loading="saveInProgress" :disabled="!password" @click="deleteBoarder") {{$t('profileDialog.DeleteAccount')}}
                          v-btn(v-if="!passwordChecked" color="primary" :loading="saveInProgress" :disabled="!password" @click="onDeleteLoginClick") {{$t('profileDialog.checkPassword')}}
          v-tab-item
            v-card(flat)
              v-form(v-model="formIsValid" ref="form" @submit.prevent="onSubmitCredentialsButtonClick" :disabled="saveInProgress")
                v-card-text.pa-0
                  v-row.ma-0
                    v-col.pb-0.pa-0.pt-4(cols="12")
                      v-text-field(outlined :label="$t('loginPage.userNameFieldLabel')" v-model="loginName" disabled)
                    v-col.pt-0.pa-0(cols="12" sm="12" md="7" lg="7")
                      v-text-field(outlined :label="$t('passwordRecovery.request.newPasswordFieldLabel')" v-model="password" :rules="passwordRules")
                    v-col.pt-3.pl-7(cols="4" v-if="this.$vuetify.breakpoint.mdAndUp")
                      v-btn(@click="generateCodeClick" medium :disabled="saveInProgress") {{$t('profileDialog.generatePassword')}}
                    v-col.text-center(cols="12" v-else)
                      v-btn(@click="generateCodeClick" medium :disabled="saveInProgress") {{$t('profileDialog.generatePassword')}}

                v-card-actions.pr-0
                  v-spacer
                  v-col.text-end.pr-0.pa-0(cols="12" sm="12" md="6" lg="6")
                    v-btn(color="primary" type="submit"  :disabled="!formIsValid" :loading="saveInProgress") {{$t('profileDialog.saveButtonLabel')}}


      v-divider
      v-card-actions
        v-spacer
        v-btn(@click="onCancelButtonClick" :disabled="isSaveInProgress || saveInProgress" text color="primary") {{$t('dashboardPage.closeButtonLabel')}}
</template>

<script lang="ts">
import Vue from 'vue'
import {Action, Getter} from 'vuex-class'
import {
  AUTH_LOG_USER_OUT, DELETE_BOARDER, DELETE_CONFIRMATION,
  FETCH_ACCOUNT_EMAILS, FETCH_ACCOUNT_USER, SET_IS_DELETE_CONFIRMATION,
  SHOW_PROFILE_DIALOG, SWITCH_APPLICATION_LANGUAGE,
  UPDATE_ACCOUNT_EMAILS,
  UPDATE_ACCOUNT_USER, UPDATE_PERSON_CREDENTIAL_PASS_BY_ID
} from '@/store/action-types'
import {
  CURRENT_UI_LANGUAGE,
  SUPPORTED_LANGUAGES,
  USER_LOGGED_IN
} from '@/store/getter-types'
import {Component, Prop} from 'vue-property-decorator'
import {TranslateResult} from 'vue-i18n'
import i18n from '@/plugins/i18n'
import _ from 'lodash'
import { AccountEmail } from '@/model/AccountEmail'
import {ChangeNewPasswordPayload, UpdateAccountEmail, UserPayload} from '@/types/payload'
import {CurrentUser} from "@/model/CurrentUser";
import Language from "@/model/Language";
import DeleteDialog from "@/components/user/DeleteDialog.vue";
import {fetchAutomaticOrderingEnabled, getSetting, persistBooleanSetting} from "@/services/settings";

const AUTO_RESERVATION_OPT_OUT_KEY = 'boarder.auto_reservation_opt_out'

function valueIsRequired (v: string): boolean | TranslateResult {
  return !!v || i18n.t('validation.valueIsRequired')
}

function validateEmail (v: string): boolean | TranslateResult {
  if (v) {
    return /.+@.+\..+/.test(v) || i18n.t('validation.emailMustBeValid')
  }
  return true
}

function validatePhoneNumber (v: string): boolean | TranslateResult {
  if (v) {
    return  /^[+]?[\d\s]+$/.test(v) || i18n.t('validation.phoneNumberMustBeValid')
  }
  return true
}

function passwordLength(v: string): boolean | TranslateResult {
  if (v) {
    return v.length >= 4 || i18n.t('validation.passwordMinimumLength')
  }

  return false
}

@Component({
  components: {
    DeleteDialog,
  }
})
export default class ProfileDialog extends Vue {

  $refs!: {
    form: Vue & { validate: () => boolean }
  }

  private originalEmailList: AccountEmail[] = []
  private emailList: AccountEmail[] = []
  private passwordRules: Array<(v: string) => string | boolean | TranslateResult> = [
    (v: string) => !!v || i18n.t('validation.valueIsRequired'),
    (v: string) => !!v || i18n.t('validation.valueIsRequiredMessage'),
    (v: string) => {
      const pattern = /^[a-zA-Z0-9]*$/

      return pattern.test(v) || i18n.t('validation.valueIsRequiredMessage')
    },
  ]
  private emailRules = [
    valueIsRequired,
    validateEmail,
  ]
  private firstNameRules = [
    valueIsRequired,
  ]
  private familyNameRules = [
    valueIsRequired,
  ]
  private validatePhoneNumber = [
    validatePhoneNumber
  ]
  private isFormValid: boolean = false
  private isSaveInProgress: boolean = false
  private isLoading: boolean = false
  private showError: boolean = false
  private errorMessage: TranslateResult | null = null
  private firstName: string = ''
  private familyName: string = ''
  private phoneNumber: string = ''
  private loginName: string = ''
  private password: string | null = null
  private formIsValid: boolean = false
  private saveInProgress: boolean = false
  private snackbar: boolean = false
  private snackBarText: TranslateResult | null = null
  private snackbarColor: string = ''
  private tab: number = 0
  private selectedItem: Language | null = null
  private showDeleteDialog: boolean = false
  private value: string = 'unActive'
  private passwordChecked: boolean = false
  private showPassword: boolean = false
  private showAutomaticOrderingSwitch: boolean = false
  private automaticOrdering: boolean = true
  private showAutomaticOrderingConfirm: boolean = false
  private automaticOrderingSaveInProgress: boolean = false


  @Action(SHOW_PROFILE_DIALOG) showProfileDialog!: { (payload: boolean): Promise<void> }
  @Action(FETCH_ACCOUNT_EMAILS) fetchAccountEmails!: ({ (): Promise<AccountEmail[]> })
  @Action(UPDATE_ACCOUNT_EMAILS) updateAccountEmails!: ({ (payload: UpdateAccountEmail[]): Promise<AccountEmail[]> })
  @Action(UPDATE_ACCOUNT_USER) updateAccountUser!: ({ (payload: UserPayload): Promise<void> })
  @Action(FETCH_ACCOUNT_USER) fetchAccountUser!: ({ (): Promise<void> })
  @Getter(USER_LOGGED_IN) currentUser!: CurrentUser
  @Getter(SUPPORTED_LANGUAGES) supportedLanguages!: Language[]
  @Getter(CURRENT_UI_LANGUAGE) currentLanguage!: Language
  @Action(UPDATE_PERSON_CREDENTIAL_PASS_BY_ID) updatePersonCredentialPassById!: { (payload: ChangeNewPasswordPayload): Promise<string> }
  @Action(SWITCH_APPLICATION_LANGUAGE) switchApplicationLanguage!: { (payload: Language): void }
  @Action(DELETE_CONFIRMATION) orderConfirmation!: ({ (payload: any): Promise<boolean> })
  @Action(SET_IS_DELETE_CONFIRMATION) setIsDeleteConfirmation!: ({ (payload: boolean): Promise<void> })
  @Action(DELETE_BOARDER) deleteBoarderAccount!: { (payload: any): Promise<any> }
  @Prop({type: Boolean, required: true}) show!: false

  get getTextFieldColor (): string {
    return this.passwordChecked ? 'checked' : ''

  }
  get passwordFieldIcon(): string {
    if(this.showPassword) {
      return 'fa-eye-slash'
    } else if(this.passwordChecked) {
      return 'fas fa-check'
    } else {
      return 'fa-eye'
    }

  }

  get passwordFieldType(): string {
    return this.showPassword ? 'text' : 'password'
  }

  get isInEditMode (): boolean {
    return !!this.emailList.find((item: AccountEmail) => item.isEdit)
  }

  get showSkeletonLoader (): boolean {
    return this.isLoading
  }

  generateCodeClick () {
    this.password = Math.floor(1000 + Math.random() * 9000).toString()
  }

  onLanguageSelect (language: Language): void {
      this.switchApplicationLanguage(language)
      localStorage.setItem('uiLanguageCodeApp', language.code)
  }

  onToggleShowPasswordClick(): void {
    this.showPassword = !this.showPassword
  }

  onAddButtonClick (): void {
    this.emailList.unshift(AccountEmail.createEmpty())
    this.originalEmailList.unshift(AccountEmail.createEmpty())
  }

  onSubmitCredentialsButtonClick () {
    this.updatePersonCredentialPassword()

  }

  onSubmitButtonClick (): void {
    this.isSaveInProgress = true
    this.save()
  }

  onCancelButtonClick (): void {
    this.closeDialog()
  }

  onCloseButtonClick (): void {
    this.closeDialog()
  }

  closeDialog (): void {
    this.showProfileDialog(false)
  }

  onExpansionClick () {
    setTimeout(() => {
      let card: any = document.querySelector("#card")
      let cardText: any = document.querySelector("#cardText")
      let options = {duration: 400, container: cardText}
      this.$vuetify.goTo(card, options)
    }, 0)
  }

  onEditClick (idx: number, item: AccountEmail): void {
    this.emailList.splice(idx, 1, item.update(!item.isEdit))
  }

  onDeleteClick (idx: number): void {
    this.emailList.splice(idx, 1)
    this.originalEmailList.splice(idx, 1)
  }

  onSaveChangesClick (idx: number, item: AccountEmail): void {
    this.$refs.form.validate()
    if(this.isFormValid && item.email) {
      this.emailList.splice(idx, 1, item.update(!item.isEdit))
      this.originalEmailList.splice(idx, 1, item.update(!item.isEdit))
    }
  }

  onCancelChangesClick (idx: number, item: AccountEmail): void {
    if(this.originalEmailList[idx].email) {
      this.emailList.splice(idx, 1, this.originalEmailList[idx])
    } else {
      this.emailList.splice(idx, 1)
      this.originalEmailList.splice(idx, 1)
    }
  }

  onDeleteLoginClick () {
    this.onLogUser()
  }

  closeDeleteDialogClick () {
    this.showDeleteDialog = false
    this.closeDialog()
    this.logUserOut()
  }

  async onLogUser(): Promise<void> {
    let response: boolean = false
    const payload: any = {
      username: this.currentUser.loginName,
      password: this.password
    }
    try{
      this.saveInProgress = true
      this.setIsDeleteConfirmation(true)
      response = await this.orderConfirmation(payload)

      this.saveInProgress = false
      this.setIsDeleteConfirmation(false)
      if (!response) {
        this.snackbarColor = "error"
        this.snackBarText = this.$t('errors.invalidCredentials').toString()
        this.snackbar = true
        this.password = ''

      } else {
        this.passwordChecked = true
      }
    } catch (e) {
      this.setIsDeleteConfirmation(false)
      this.snackbarColor = "error"
      this.snackBarText = this.$t('errors.invalidCredentials').toString()
      this.snackbar = true
    }
  }

  async logUserOut():Promise<void> {
    await this.$store.dispatch(AUTH_LOG_USER_OUT)
    this.$router.push('/login')
  }


  async fetchData (): Promise<void> {
    // any rejection here used to leave the skeleton loader up forever
    try {
      const data: AccountEmail[] = await this.fetchAccountEmails()
      this.originalEmailList = _.cloneDeep(data)
      this.emailList = _.cloneDeep(data)
      this.firstName = this.currentUser.givenName
      this.familyName = this.currentUser.familyName
      this.phoneNumber = this.currentUser.phoneNumbers[0]
      this.loginName = this.currentUser.loginName
      await this.fetchAutomaticOrderingState()
    } catch (e) {
      console.error('profile dialog data load failed', e)
      this.errorMessage = this.$t('errors.fetchDataError')
      this.showError = true
    } finally {
      this.isLoading = false
    }
  }

  // the switch is shown only when the tenant orders automatically at all
  async fetchAutomaticOrderingState (): Promise<void> {
    try {
      this.showAutomaticOrderingSwitch = await fetchAutomaticOrderingEnabled()
      this.automaticOrdering = getSetting(AUTO_RESERVATION_OPT_OUT_KEY)?.value !== 'true'
    } catch (e) {
      this.showAutomaticOrderingSwitch = false
    }
  }

  onAutomaticOrderingChange (value: boolean): void {
    if (!value) {
      // switching automatic ordering OFF needs an explicit confirmation
      this.showAutomaticOrderingConfirm = true
      return
    }

    this.persistAutomaticOrdering(true)
  }

  async onAutomaticOrderingConfirm (): Promise<void> {
    await this.persistAutomaticOrdering(false)
    this.showAutomaticOrderingConfirm = false
  }

  onAutomaticOrderingCancel (): void {
    this.showAutomaticOrderingConfirm = false
    this.automaticOrdering = true
  }

  private async persistAutomaticOrdering (enabled: boolean): Promise<void> {
    this.automaticOrderingSaveInProgress = true
    try {
      await persistBooleanSetting(AUTO_RESERVATION_OPT_OUT_KEY, !enabled)
      this.automaticOrdering = enabled
      this.snackbarColor = "success"
      this.snackBarText = this.$t(enabled ? 'profileDialog.automaticOrderingEnabledMessage' : 'profileDialog.automaticOrderingDisabledMessage')
      this.snackbar = true
    } catch (e) {
      this.automaticOrdering = !enabled
      this.errorMessage = this.$t('profileDialog.saveErrorMessage')
      this.showError = true
    } finally {
      this.automaticOrderingSaveInProgress = false
    }
  }

  async save(): Promise<void> {
    const payload: UpdateAccountEmail[] =  this.emailList.map((item: AccountEmail) => {
      return {email: item.email}
    })

    const userPayload: UserPayload = {
      firstName: this.firstName,
      familyName: this.familyName,
      phoneNumber: this.phoneNumber
    }

    try {
      await this.updateAccountUser(userPayload)
      this.emailList = await this.updateAccountEmails(payload)
      await this.fetchAccountUser()
    } catch (e) {
      this.errorMessage = this.$t('profileDialog.saveErrorMessage')
      this.showError = true
    } finally {
      this.isSaveInProgress = false
    }
  }

  async updatePersonCredentialPassword(): Promise<void>{
    const payload: ChangeNewPasswordPayload = {
      personId: this.currentUser.id,
      password: this.password!.toString(),
    }

    try {
      this.saveInProgress = true
      await this.updatePersonCredentialPassById(payload)
      this.snackbarColor = "success"
      this.snackBarText =this.$t('profileDialog.savedPassword')
      this.snackbar = true
    } catch (e) {
      this.snackbarColor = "error"
      this.snackBarText = this.$t('profileDialog.savePasswordErrorMessage')
      this.snackbar = true
    } finally {
      this.saveInProgress = false
    }
  }

  async deleteBoarder ():Promise<void> {
    const payload: any | null = this.currentUser.id
    this.saveInProgress = true
    try {
      const operationResult = await this.deleteBoarderAccount(payload)
      this.showDeleteDialog = true
      this.saveInProgress = false
    } catch (e) {
      this.snackbarColor = "error"
      this.snackBarText = this.$t('profileDialog.deleteErrorMessage').toString()
      this.snackbar = true

    } finally {
      this.saveInProgress = false
      this.passwordChecked = false

    }
  }

  mounted() {
    this.isLoading = true
    this.fetchData()
    this.selectedItem = this.currentLanguage
  }

}
</script>

<style lang="stylus" scoped>
.card-body
  min-height 200px
  overflow auto

.sloader
  width 100%

.heading
  scroll-margin-top 72px

.expansion-panel
  border 2px solid #FF0266 !important;

.checked
  >>> .v-input__slot fieldset
    border-color #4caf50 !important
  >>> .theme--light.v-icon
    color #4caf50 !important

</style>
